import React, { useRef, useEffect, useState, useContext } from "react";
import { Box } from "@material-ui/core";
import {
  HotelShopRoomTypePickerRedesign,
  HotelShopRoomTypePickerEnum,
  HotelShopRoomTypePickerVariant,
  getFirstPayNowRoomAndProductIndex,
} from "halifax";
import { RouteComponentProps } from "react-router";
import {
  PAYMENT_SCHEDULE_SELECTED,
  RoomInfoProducts,
  SELECT_PC_ROOM,
  VIEWED_PAYMENT_SCHEDULE_MODAL,
} from "redmond";
import { RoomPaymentSchedule } from "redmond/hotels-module/interfaces";
import { MobileRoomPickerConnectorProps } from "./container";
import { MobileShopHeader } from "../MobileShopHeader";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ClientContext } from "../../../../App";
import "./styles.scss";

import { HotelRoomPaymentScheduleModal } from "../HotelRoomPaymentScheduleModal";
import { config } from "../../../../api/config";
import { isCorpTenant, useShowPolicyBanner } from "@capone/common";
import { useExperiments, getExperimentVariantJSON, ALLOWED_PAY_LATER_LODGINGS, AllowedPayLaterLodgingsConfig } from "../../../../context/experiments";
import { hasPayLaterProducts, isAllowedPayLaterLodging } from "../../utils/payLater";

export interface MobileRoomPickerProps
  extends MobileRoomPickerConnectorProps,
    RouteComponentProps {
  roomInfoProductsType?: HotelShopRoomTypePickerVariant;
  handleReadyToRedirect?: () => void;
  isGlobalMobileNavExperiment?: boolean;
  isVRForPremiumCardHoldersEnabled: boolean;
  nonRefundablePolicyOverrideText?: string;
  isFintechHotelUpdatedUxEnabled?: boolean;
}

export const MobileRoomPicker = (props: MobileRoomPickerProps) => {
  const clientContext = useContext(ClientContext);
  const { isAutoApprovalEnabled, policies } = clientContext;
  const expState = useExperiments();

  const {
    checkinDate,
    checkoutDate,
    history,
    fetchPremierCollectionShop,
    chosenProduct,
    selectRoomType,
    chosenRoomInfoIndex,
    roomInfoProductsType,
    handleReadyToRedirect,
    isGlobalMobileNavExperiment,
    isVRForPremiumCardHoldersEnabled,
    isFintechHotelUpdatedUxEnabled = false,
    selectedLodging,
    roomInfoProducts,
    selectedRewardsAccount,
  } = props;
  const isFirstUpdate = useRef<boolean>(true);
  const showPolicyBanner = useShowPolicyBanner(policies);

  const [isOpenRoomPaymentScheduleModal, setOpenRoomPaymentScheduleModal] =
    useState(false);

  const [modalRoomInfoProducts, setModalRoomInfoProducts] = useState<
    undefined | RoomInfoProducts
  >(undefined);

  const showGlobalMobileNavExperience =
      isGlobalMobileNavExperiment && !isCorpTenant(config.TENANT);

  const allowedPayLaterLodgingsConfig = getExperimentVariantJSON(
    expState.experiments,
    ALLOWED_PAY_LATER_LODGINGS
  ) as AllowedPayLaterLodgingsConfig | null;

  useEffect(() => {
    if (checkinDate && checkoutDate) {
      // skip the first update
      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
      } else {
        fetchPremierCollectionShop(history, {
          forceCallHotelAvailability: true,
        });
      }
    }
  }, [checkinDate, checkoutDate]);

  useEffect(() => {
    if (!chosenProduct) {
      const { roomInfoIndex, productIndex } =
        getFirstPayNowRoomAndProductIndex(roomInfoProducts);
      selectRoomType(roomInfoIndex, productIndex);
    }
    setTimeout(() => {
      const id = "chosen-room";
      const yOffset = -122.5;
      const element = document.getElementById(id);
      const y =
        (element?.getBoundingClientRect().top ?? 0) +
        window.pageYOffset +
        yOffset;
      if (chosenRoomInfoIndex || chosenRoomInfoIndex === 0) {
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 50);
  }, []);

  return (
    <Box>
      <MobileShopHeader
        isVRForPremiumCardHoldersEnabled={isVRForPremiumCardHoldersEnabled}
      />
      {/* {isTravelWalletOfferExperiment &&
        selectedLodging?.bestOfferThisLodging && (
          <TravelWalletDetailsBanner
            offer={selectedLodging?.bestOfferThisLodging}
            className="mobile-premierCollection-shop-offer"
            screen={SelectedTravelOfferScreen.HOTEL_DETAILS}
          />
        )} */}
      <HotelShopRoomTypePickerRedesign
        {...props}
        roomInfoProductsType={
          roomInfoProductsType ?? {
            roomInfoProducts,
            variant: HotelShopRoomTypePickerEnum.Default,
          }
        }
        isMobile
        className="b2b"
        onClickContinue={(_, selectedRoomInfo?: RoomInfoProducts) => {
          trackEvent({
            eventName: SELECT_PC_ROOM,
            properties: {
              room_type:
                props.chosenRoomInfo && props.chosenRoomInfo.roomInfo.name,
            },
          });

          const shouldOpenPaymentScheduleModal = () => {
            const hasPayLater = hasPayLaterProducts(
              selectedRoomInfo?.products
            );
            const lodgingAllowed = isAllowedPayLaterLodging(
              allowedPayLaterLodgingsConfig?.data?.lodgingIds,
              selectedLodging?.lodging.id
            );

            return !!selectedLodging && hasPayLater && lodgingAllowed;
          };

          const openPaymentScheduleModal = () => {
            const paymentSchedules = new Set(
              selectedRoomInfo?.products.map(
                (p) => p.roomPaymentSchedule ?? RoomPaymentSchedule.PayNow
              )
            );

            trackEvent({
              eventName: VIEWED_PAYMENT_SCHEDULE_MODAL,
              properties: {
                payment_schedule_available: Array.from(paymentSchedules)
                  .sort()
                  .join(),
                stage: "details_page",
              },
            });
            setModalRoomInfoProducts(selectedRoomInfo);
            setOpenRoomPaymentScheduleModal(true);
          };

          if (shouldOpenPaymentScheduleModal()) {
            openPaymentScheduleModal();
          } else if (handleReadyToRedirect) {
            handleReadyToRedirect();
          }
        }}
        isGlobalMobileNavExperiment={showGlobalMobileNavExperience}
        variant={
          isFintechHotelUpdatedUxEnabled ? "refundable-room-ux" : "default"
        }
        isApprovalRequired={!isAutoApprovalEnabled}
        rewardsAccount={selectedRewardsAccount}
        showPolicyBanner={showPolicyBanner}
      />

      {modalRoomInfoProducts && (
        <HotelRoomPaymentScheduleModal
          openModal={isOpenRoomPaymentScheduleModal}
          onClose={() => {
            setOpenRoomPaymentScheduleModal(false);
            setModalRoomInfoProducts(undefined);
          }}
          onClickContinue={(selectedProductIndex: number) => {
            trackEvent({
              eventName: PAYMENT_SCHEDULE_SELECTED,
              properties: {
                payment_schedule:
                  modalRoomInfoProducts.products[selectedProductIndex]
                    .roomPaymentSchedule ?? RoomPaymentSchedule.PayNow,
                stage: "details_page",
              },
            });
            selectRoomType(chosenRoomInfoIndex, selectedProductIndex);
            setOpenRoomPaymentScheduleModal(false);
            setModalRoomInfoProducts(undefined);

            if (handleReadyToRedirect) {
              handleReadyToRedirect();
            }
          }}
          roomInfoProducts={modalRoomInfoProducts}
          isMobile
        />
      )}
    </Box>
  );
};
